const chartTypes = ['line', 'bar']
const operators = ['AVG', 'SUM', 'COUNT', 'MAX', 'MIN']
const numericTypes = ['int', 'long', 'float', 'double', 'short', 'byte', 'decimal', 'number']
const present = value => value !== undefined && value !== null && value !== ''

export function isDeviceChart (node) {
  return !!node && Number(node.dataType) === 2 && !!node.component && chartTypes.includes(node.component.prop) && node.component.name === node.component.prop
}

export function chartMode (parameter = {}) {
  const mode = Number(parameter.configDataType === undefined ? 0 : parameter.configDataType)
  if (![0, 1, 2].includes(mode)) throw new Error('设备图表数据类型不正确，请重新选择')
  return mode
}

export function chartDeviceId (parameter = {}, context = {}, editing = false) {
  // 示例设备只在编辑器有效，运行态缺少页面 ID 时不能偷偷查固定设备。
  const value = parameter.deviceSource === 'page' && !editing ? context.deviceId : parameter.deviceId
  if (!present(value)) throw new Error(parameter.deviceSource === 'page'
    ? (editing ? '请选择编辑时使用的示例设备' : '当前页面没有设备 ID，请从设备列表进入') : '请选择设备')
  return String(value)
}

export function chartLimit (parameter = {}) {
  const value = Number(parameter.limit)
  // 兼容旧模板 limit=0；所有查询都有上限，不能把无限历史一次拉入大屏。
  if (!present(parameter.limit) || value === 0) return 200
  if (!Number.isInteger(value) || value < 1 || value > 10000) throw new Error('数据上限应为 1～10000 的整数')
  return value
}

export function queryTime (value, fallback, now) {
  if (!present(value)) return fallback
  if (typeof value === 'number' || /^\d{11,13}$/.test(String(value))) {
    const result = Number(value)
    if (Number.isFinite(result) && !Number.isNaN(new Date(result).getTime())) return result
  }
  const relative = String(value).trim().match(/^now(?:([+-])(\d+)(s|m|h|d|w|M|y))?$/)
  if (relative) {
    if (!relative[1]) return now
    const amount = Number(relative[2]) * (relative[1] === '-' ? -1 : 1)
    const unit = relative[3]
    const date = new Date(now)
    if (unit === 'M') date.setMonth(date.getMonth() + amount)
    else if (unit === 'y') date.setFullYear(date.getFullYear() + amount)
    else return now + amount * ({ s: 1000, m: 60000, h: 3600000, d: 86400000, w: 604800000 }[unit])
    if (Number.isFinite(date.getTime())) return date.getTime()
  }
  const time = Date.parse(String(value).trim().replace(/^(\d{4}-\d\d-\d\d) /, '$1T'))
  if (Number.isFinite(time)) return time
  throw new Error('时间格式不正确，请选择日期或填写 now-7d、now 等时间表达式')
}

export function chartTimeRange (parameter = {}, now = Date.now()) {
  if (parameter.rangeMode === 'fixed' && (!present(parameter.from) || !present(parameter.to))) throw new Error('请选择完整的开始和结束时间')
  const from = queryTime(parameter.from, now - 7 * 86400000, now)
  const to = queryTime(parameter.to, now, now)
  if (from >= to) throw new Error('结束时间必须晚于开始时间')
  return { from, to }
}

export function deviceChartQuery (parameter = {}, context = {}, editing = false, now = Date.now()) {
  const mode = chartMode(parameter)
  const deviceId = chartDeviceId(parameter, context, editing)
  if (!present(parameter.property)) throw new Error('请选择设备属性')
  const property = String(parameter.property)
  const limit = chartLimit(parameter)
  const range = chartTimeRange(mode === 2 && !present(parameter.from) ? { ...parameter, from: 'now-1h' } : parameter, now)
  if (mode !== 1) return { mode, deviceId, property, limit, range, data: {
    pageIndex: 0, pageSize: limit,
    sorts: [{ name: 'timestamp', order: 'desc' }],
    terms: [
      { column: 'timestamp', termType: 'gte', value: range.from },
      { column: 'timestamp', termType: 'lte', value: range.to }
    ]
  } }
  const agg = String(parameter.aggregationType || 'AVG').toUpperCase()
  if (!operators.includes(agg)) throw new Error('聚合方式不正确，请选择 AVG、SUM、COUNT、MAX 或 MIN')
  const interval = String(parameter.interval || parameter.time || '1h')
  if (!/^[1-9]\d*(s|m|h|d|w|M|y)$/.test(interval)) throw new Error('时间间隔不正确，例如 10m、1h 或 1d')
  if (Number(interval.slice(0, -1)) > 1000000) throw new Error('时间间隔过大，请缩小间隔')
  return { mode, deviceId, property, limit, range, data: {
    columns: [{ property, alias: property, agg }],
    query: { ...range, interval, format: parameter.format || 'yyyy-MM-dd HH:mm:ss', limit }
  } }
}

export function responseBody (response) {
  const body = response && response.data
  if (!body || typeof body !== 'object') throw new Error('平台返回的数据格式不正确')
  if (body.success === false || (present(body.status) && Number(body.status) !== 200)) {
    throw new Error(body.message || '平台查询失败')
  }
  return body
}

export function propertyRows (body) {
  const result = Object.prototype.hasOwnProperty.call(body, 'result') ? body.result : body
  if (Array.isArray(result)) return result
  if (result && Array.isArray(result.data)) return result.data
  throw new Error('平台没有返回属性记录列表')
}

export function chartProperties (metadata) {
  let model = metadata
  if (typeof model === 'string') {
    try { model = JSON.parse(model) } catch (error) { throw new Error('物模型不是有效的 JSON') }
  }
  if (!model || !Array.isArray(model.properties)) return []
  return model.properties.map(property => {
    const valueType = property.valueType || {}
    return { id: property.id, name: property.name || property.id,
      type: valueType.type || property.dataType || '', unit: typeof valueType.unit === 'string' ? valueType.unit : (valueType.unit || {}).symbol || '' }
  }).filter(property => property.id)
}

export function numericProperty (property) { return numericTypes.includes(property.type) }

function numberValue (value) {
  if (!present(value) || typeof value === 'boolean') return null
  const result = Number(value)
  return Number.isFinite(result) ? result : null
}

export function timeLabel (timestamp, format = 'yyyy-MM-dd HH:mm:ss') {
  const date = new Date(timestamp)
  if (!Number.isFinite(date.getTime())) return ''
  const pad = n => String(n).padStart(2, '0')
  const parts = { yyyy: String(date.getFullYear()), MM: pad(date.getMonth() + 1), dd: pad(date.getDate()),
    HH: pad(date.getHours()), hh: pad(date.getHours()), mm: pad(date.getMinutes()), ss: pad(date.getSeconds()) }
  return format.replace(/yyyy|MM|dd|HH|hh|mm|ss/g, token => parts[token])
}

function rowTime (row, format) {
  const value = present(row.timestamp) ? row.timestamp : present(row.time) ? row.time : row.timeString
  if (!present(value)) return { order: null, label: '' }
  if (typeof value === 'number' || /^\d{11,13}$/.test(String(value))) {
    return { order: Number(value), label: timeLabel(Number(value), format) }
  }
  // 支持 yyyy-MM-dd 等后端格式，不能按 dd/MM/yyyy 的字面顺序排列。
  const tokens = [], pattern = format.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/yyyy|MM|dd|HH|hh|mm|ss/g, token => { tokens.push(token); return token === 'yyyy' ? '(\\d{4})' : '(\\d{1,2})' })
  const matched = String(value).match(new RegExp('^' + pattern + '$'))
  let order = Date.parse(String(value).replace(/^(\d{4}-\d\d-\d\d) /, '$1T'))
  if (matched) {
    const parts = { yyyy: 1970, MM: 1, dd: 1, HH: 0, mm: 0, ss: 0 }
    tokens.forEach((token, i) => { parts[token === 'hh' ? 'HH' : token] = Number(matched[i + 1]) })
    order = new Date(parts.yyyy, parts.MM - 1, parts.dd, parts.HH, parts.mm, parts.ss).getTime()
  }
  return { order: Number.isFinite(order) ? order : null, label: String(value) }
}

export function chartPoints (body, parameter = {}) {
  const mode = chartMode(parameter), format = parameter.format || 'yyyy-MM-dd HH:mm:ss'
  return propertyRows(body).map((row, index) => {
    const time = rowTime(row, format)
    // WS 的 MeasurementValue.value 包着 DeviceProperty / createValue 映射，
    // 历史属性接口则直接返回 DeviceProperty。不能把对象转成数值。
    const value = mode === 1 ? row[parameter.property]
      : mode === 2 && row.value && typeof row.value === 'object' ? row.value.value : row.value
    return { row, index, ...time, rawValue: value, value: numberValue(value) }
  })
}

export function defaultChartData (body, parameter = {}) {
  const points = chartPoints(body, parameter).sort((a, b) => {
    if (a.order !== null && b.order !== null) return a.order - b.order || a.index - b.index
    return a.index - b.index
  })
  return { categories: points.map(point => point.label), series: [{
    name: parameter.propertyName || parameter.property || '设备属性', data: points.map(point => point.value)
  }] }
}

export function historyRealtimeRows (body, parameter = {}) {
  return propertyRows(body).map(row => ({
    timestamp: row.timestamp, timeString: timeLabel(row.timestamp, parameter.format), value: { ...row }
  }))
}

export function mergeRealtimeRows (previous, incoming, limit) {
  const rows = new Map()
  // 相同时间点以最新推送为准；HTTP 初始查询与 WS 缓冲重叠时不会多画一个点。
  previous.concat(incoming).forEach((row, index) => {
    const time = rowTime(row, 'yyyy-MM-dd HH:mm:ss')
    rows.set(time.order === null ? 'unknown-' + index : time.order, { row, order: time.order, index })
  })
  return Array.from(rows.values()).sort((a, b) => {
    if (a.order !== null && b.order !== null) return a.order - b.order || a.index - b.index
    return a.index - b.index
  }).slice(-limit).map(item => item.row)
}

export function legacyChartInput (body, parameter = {}) {
  // 旧设备 dashboard 的 data.value 是属性对象（数值在 value.value），不是标量。
  // 对齐旧平台：history 使用 sort() 升序，agg 使用 Comparable 默认降序。
  // 只适配输入，formatter 的输出不再排序或二次转换。
  const mode = chartMode(parameter)
  const result = chartPoints(body, parameter).sort((a, b) => {
    if (a.order !== null && b.order !== null) return (mode === 1 ? b.order - a.order : a.order - b.order) || a.index - b.index
    return a.index - b.index
  }).map(point => ({
    data: { timeString: point.label, value: { propertyName: parameter.propertyName, unit: parameter.propertyUnit,
      ...point.row, property: parameter.property, value: point.rawValue }, timestamp: point.order }
  }))
  return { ...body, result }
}

export function validateChartData (data) {
  if (!data || !Array.isArray(data.categories) || !Array.isArray(data.series) ||
    data.series.some(series => !series || !Array.isArray(series.data) || series.data.length !== data.categories.length)) {
    throw new Error('数据处理必须返回 categories 数组和 series 数组，且每个系列的数据长度与时间轴一致')
  }
  return data
}

export function transformDeviceChart (body, parameter, formatter, preview = false) {
  if (!preview && typeof formatter === 'function') {
    const input = parameter.formatterInput === 'raw' ? body : legacyChartInput(body, parameter)
    return validateChartData(formatter(input))
  }
  return defaultChartData(body, parameter)
}
